import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Search, Workflow, X } from "lucide-react";
import { useFluxos, type Fluxo } from "@/hooks/useFluxos";
import { BLOCK_META } from "@/components/fluxos/blockMeta";

interface FluxosPickerProps {
  onClose: () => void;
  onSelect?: (fluxo: Fluxo) => void;
}

export const FluxosPicker = ({ onClose, onSelect }: FluxosPickerProps) => {
  const { fluxos, loading } = useFluxos();
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return fluxos;
    return fluxos.filter((fluxo) => {
      const blockLabels = fluxo.blocks.map((b) => BLOCK_META[b.type].label).join(" ");
      return (
        fluxo.name.toLowerCase().includes(term) ||
        blockLabels.toLowerCase().includes(term)
      );
    });
  }, [fluxos, search]);

  const handleSelect = (fluxo: Fluxo) => {
    onSelect?.(fluxo);
    onClose();
  };

  return (
    <div className="w-[380px] max-h-[480px] bg-white border border-[#E5E7EB] rounded-xl shadow-sm flex flex-col overflow-hidden transition-colors duration-200 dark:bg-[#27272A] dark:border-[#3F3F46] dark:shadow-none">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E7EB] dark:border-[#3F3F46]">
        <h3 className="text-sm font-medium text-[#0F172A] dark:text-[#FAFAFA]">Fluxos</h3>
        <button
          type="button"
          onClick={onClose}
          className="text-[#64748B] hover:text-[#2563EB] transition-colors duration-200 dark:text-[#A1A1AA] dark:hover:text-[#60A5FA]"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="px-3 py-2 border-b border-[#E5E7EB] dark:border-[#3F3F46]">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#94A3B8] dark:text-[#71717A]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar fluxo..."
            className="h-8 pl-8 text-sm rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] transition-colors duration-200 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA]"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-10 text-center px-4">
            <div className="w-6 h-6 border-2 border-[#E5E7EB] border-t-[#2563EB] rounded-full animate-spin mb-3 dark:border-[#3F3F46] dark:border-t-[#60A5FA]" />
            <p className="text-sm text-[#94A3B8] dark:text-[#71717A]">Carregando fluxos...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center px-4">
            <div className="w-10 h-10 rounded-full bg-[#EEF4FF] flex items-center justify-center mb-3 dark:bg-blue-500/15">
              <Workflow className="w-5 h-5 text-[#2563EB] dark:text-[#60A5FA]" />
            </div>
            <p className="text-sm text-[#94A3B8] dark:text-[#71717A]">
              {search.trim() ? "Nenhum fluxo encontrado" : "Nenhum fluxo cadastrado"}
            </p>
            {!search.trim() && (
              <p className="text-xs text-[#94A3B8] mt-1 dark:text-[#71717A]">
                Crie fluxos em Fluxos no menu lateral
              </p>
            )}
          </div>
        ) : (
          <div className="py-1">
            {filtered.map((fluxo) => {
              const blockSummary =
                fluxo.blocks.map((b) => BLOCK_META[b.type].label).join(" • ") || "Sem blocos";
              return (
                <button
                  key={fluxo.id}
                  type="button"
                  onClick={() => handleSelect(fluxo)}
                  className="w-full text-left px-4 py-3 hover:bg-[#F8FAFC] transition-colors duration-200 flex items-center gap-3 border-b border-[#E5E7EB]/60 last:border-b-0 dark:hover:bg-[#3F3F46]/50 dark:border-[#3F3F46]/50"
                >
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-[#EEF4FF] dark:bg-blue-500/15">
                    <Workflow className="w-3.5 h-3.5 text-[#2563EB] dark:text-[#60A5FA]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-[13px] font-medium text-[#0F172A] truncate dark:text-[#FAFAFA]">
                        {fluxo.name}
                      </p>
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#F1F5F9] text-[#64748B] flex-shrink-0 dark:bg-[#3F3F46] dark:text-[#A1A1AA]">
                        {fluxo.blocks.length} {fluxo.blocks.length === 1 ? "bloco" : "blocos"}
                      </span>
                    </div>
                    <p className="text-xs text-[#64748B] line-clamp-1 dark:text-[#A1A1AA]">
                      {blockSummary}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default FluxosPicker;
